import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Gift, Plus, Save, X, Edit2, CheckCircle, AlertCircle, RefreshCw, Package, Truck, Ban } from 'lucide-react';

interface GiftCatalogItem {
  id: number;
  key: string;
  name: string;
  description: string;
  isActive: number;
}

interface PendingGift {
  id: number;
  phone: string;
  giftName?: string;
  giftKey?: string;
  description: string;
  quantity: number;
  status: 'pending' | 'attached' | 'delivered' | 'cancelled';
  sourceEvent?: string;
  attachedOrderId?: number;
  createdAt: number;
}

const STATUS_LABELS: Record<string, string> = {
  pending: '待送出',
  attached: '已隨單',
  delivered: '已交付',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  attached: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

export default function AdminPendingGifts() {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState<PendingGift[]>([]);
  const [catalog, setCatalog] = useState<GiftCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ phone: '', giftKey: '', description: '' });

  const fetchCatalog = async () => {
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (!res.ok) return;
      const data = await res.json();
      setCatalog((Array.isArray(data) ? data : data.data || []).filter((g: GiftCatalogItem) => g.isActive));
    } catch (e) { console.error(e); }
  };

  const fetchGifts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/pending-gifts?status=${statusFilter}`);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setGifts(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchCatalog();
    fetchGifts();
  }, [navigate]);

  useEffect(() => {
    fetchGifts();
  }, [statusFilter]);

  const showToast = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const saveGift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.phone.trim() || !form.giftKey.trim()) {
      showToast('請填寫電話與禮品', true); return;
    }
    try {
      const res = await apiFetch('/api/public/admin/pending-gifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: form.phone.trim(),
          giftKey: form.giftKey.trim(),
          description: form.description.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast('已建立待送禮品');
      setModalOpen(false);
      setForm({ phone: '', giftKey: '', description: '' });
      fetchGifts();
    } catch (err: any) { showToast(err.message || '儲存失敗', true); }
  };

  const updateStatus = async (id: number, status: string) => {
    if (!confirm(`確定將禮品標記為「${STATUS_LABELS[status]}」？`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/pending-gifts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '更新失敗');
      showToast('已更新狀態');
      fetchGifts();
    } catch (err: any) { showToast(err.message || '更新失敗', true); }
  };

  const formatDate = (ts?: number) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout currentPage="pending-gifts">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Gift className="w-6 h-6 text-red-600" />
          待送禮品總覽
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          查看所有掛在客戶名下的禮品，包含待送出、已隨單、已交付與已取消的記錄。
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">狀態：</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
          >
            <option value="pending">待送出</option>
            <option value="attached">已隨單</option>
            <option value="delivered">已交付</option>
            <option value="cancelled">已取消</option>
            <option value="">全部</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchGifts} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
            <RefreshCw size={16} /> 重新整理
          </button>
          <button onClick={() => setModalOpen(true)} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
            <Plus size={16} /> 新增待送禮品
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : gifts.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          沒有符合條件的禮品記錄
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 text-left font-medium">ID</th>
                <th className="px-4 py-3 text-left font-medium">電話</th>
                <th className="px-4 py-3 text-left font-medium">禮品</th>
                <th className="px-4 py-3 text-left font-medium">來源</th>
                <th className="px-4 py-3 text-left font-medium">建立時間</th>
                <th className="px-4 py-3 text-left font-medium">狀態</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {gifts.map((g) => (
                <tr key={g.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{g.id}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{g.phone}</td>
                  <td className="px-4 py-3">
                    <div className="text-gray-900 dark:text-white">{g.giftName || g.description || '禮品'}</div>
                    {g.description && g.description !== g.giftName && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{g.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {g.sourceEvent || '-'}
                    {g.attachedOrderId ? (
                      <div className="text-xs mt-1">
                        <button
                          onClick={() => navigate(`/admin/orders/${g.attachedOrderId}`)}
                          className="text-red-600 hover:underline"
                        >
                          訂單 #{g.attachedOrderId}
                        </button>
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(g.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[g.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[g.status] || g.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {(g.status === 'pending' || g.status === 'attached') && (
                        <button
                          onClick={() => updateStatus(g.id, 'delivered')}
                          className="text-green-600 hover:text-green-700 p-1"
                          title="標記已交付"
                        >
                          <Truck size={16} />
                        </button>
                      )}
                      {g.status === 'pending' && (
                        <button
                          onClick={() => updateStatus(g.id, 'cancelled')}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="取消"
                        >
                          <Ban size={16} />
                        </button>
                      )}
                      {g.attachedOrderId && (
                        <button
                          onClick={() => navigate(`/admin/orders/${g.attachedOrderId}`)}
                          className="text-blue-600 hover:text-blue-700 p-1"
                          title="查看訂單"
                        >
                          <Package size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-xl w-full">
            <form onSubmit={saveGift} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">新增待送禮品</h2>
                <button type="button" onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">客戶電話 *</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="例如 85260771493"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">禮品 *</label>
                  <select
                    value={form.giftKey}
                    onChange={(e) => setForm({ ...form, giftKey: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">請選擇</option>
                    {catalog.map((item) => (
                      <option key={item.key} value={item.key}>{item.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備註（可選）</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"><Save size={16} /> 儲存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
